import { useAuth } from '@/hooks/useAuth';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Building, Users, FolderOpen, BarChart3, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import Dashboard from './Dashboard';

const Index = () => {
  const { user, loading } = useAuth();

  // Show loading spinner while checking auth
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // If user is authenticated, show the dashboard
  if (user) {
    return (
      <DashboardLayout>
        <Dashboard />
      </DashboardLayout>
    );
  }

  // If user is not authenticated, show landing page with login option
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Building className="h-8 w-8 text-primary" />
            <h1 className="text-2xl font-bold">BuildCRM</h1>
          </div>
          <div className="flex items-center gap-4">
            <Button variant="ghost" asChild>
              <Link to="/auth">Sign In</Link>
            </Button>
            <Button asChild>
              <Link to="/auth">Get Started</Link>
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <main className="container mx-auto px-4 py-16">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold tracking-tight mb-6">
            Manage Your Construction Business with Ease
          </h2>
          <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
            BuildCRM helps construction professionals track leads, manage projects, 
            and grow their business with powerful tools designed specifically for the construction industry.
          </p>
          <div className="flex gap-4 justify-center">
            <Button size="lg" asChild>
              <Link to="/auth" className="flex items-center gap-2">
                Start Free Trial
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
            <Button variant="outline" size="lg" asChild>
              <Link to="/auth">Sign In</Link>
            </Button>
          </div>
        </div>

        {/* Features */}
        <div className="grid gap-8 md:grid-cols-3 mb-16">
          <Card>
            <CardHeader>
              <Users className="h-8 w-8 text-primary mb-2" />
              <CardTitle>Lead Management</CardTitle>
              <CardDescription>
                Track and nurture leads from first contact to project completion
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="text-sm text-muted-foreground space-y-2">
                <li>• Contact information tracking</li>
                <li>• Lead status management</li>
                <li>• Estimated value tracking</li>
                <li>• Source attribution</li>
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <FolderOpen className="h-8 w-8 text-primary mb-2" />
              <CardTitle>Project Tracking</CardTitle>
              <CardDescription>
                Manage construction projects from planning to completion
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="text-sm text-muted-foreground space-y-2">
                <li>• Project timeline management</li>
                <li>• Budget tracking</li>
                <li>• Status updates</li>
                <li>• Client communication</li>
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <BarChart3 className="h-8 w-8 text-primary mb-2" />
              <CardTitle>Business Analytics</CardTitle>
              <CardDescription>
                Get insights into your business performance and growth
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="text-sm text-muted-foreground space-y-2">
                <li>• Revenue tracking</li>
                <li>• Lead conversion rates</li>
                <li>• Project profitability</li>
                <li>• Performance metrics</li>
              </ul>
            </CardContent>
          </Card>
        </div>

        {/* CTA Section */}
        <div className="text-center bg-muted rounded-lg p-8">
          <h3 className="text-2xl font-bold mb-4">Ready to Get Started?</h3>
          <p className="text-muted-foreground mb-6">
            Join construction professionals who are already using BuildCRM to grow their business.
          </p>
          <Button size="lg" asChild>
            <Link to="/auth" className="flex items-center gap-2">
              Create Your Account
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-border mt-16">
        <div className="container mx-auto px-4 py-8 text-center text-muted-foreground">
          <p>&copy; 2025 BuildCRM. Built for construction professionals.</p>
        </div>
      </footer>
    </div>
  );
};

export default Index;
